import { ArzobispadoClientPage } from "@/components/arzobispado/ArzobispadoClientPage";
import { getAllSocialPosts, getAllActiveStories } from "@/lib/actions";
import { Organizacion } from "@/lib/types";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ArzobispadoPage() {
    const { user, userData, loading: authLoading, jartinetData, handleActionCompletion } = useAuth();
    const [pageData, setPageData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (!user?.uid) {
                setPageData({ isAuthorized: false, error: "Not authenticated" });
                setLoading(false);
                return;
            }
            
            const arzobispadoData = jartinetData.organizaciones.find(o => o.nombreCompleto === 'Arzobispado');

            if (!arzobispadoData) {
                setPageData({ isAuthorized: false, initialArzobispado: null, error: "Arzobispado not found" });
                setLoading(false);
                return;
            }

            const isGestor = arzobispadoData.gestores?.includes(user.uid);
            const isAdmin = userData?.role === 'admin';

            if (!isGestor && !isAdmin) {
                setPageData({ isAuthorized: false, initialArzobispado: arzobispadoData, error: "Not authorized" });
                setLoading(false);
                return;
            }

            const [postsResult, storiesResult] = await Promise.all([
                getAllSocialPosts(),
                getAllActiveStories(),
            ]);

            setPageData({
                isAuthorized: true,
                initialArzobispado: arzobispadoData,
                allHermandades: jartinetData.hermandades || [],
                allBandas: jartinetData.bandas || [],
                allPosts: postsResult.posts || [],
                allStories: storiesResult.stories || [],
                allOrganizaciones: jartinetData.organizaciones || [],
                currentUser: { uid: user.uid },
                error: null,
                onAction: handleActionCompletion,
            });
        } catch (e: any) {
            console.error("Error fetching arzobispado data:", e);
            setPageData({ isAuthorized: false, error: `Failed to fetch data: ${e.message}` });
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (!authLoading && user && jartinetData.organizaciones.length > 0) {
            fetchData();
        } else if (!authLoading) {
            // This handles the case where there's no user or jartinetData is still empty
            setLoading(false);
        }
    }, [authLoading, user, jartinetData]);


    if (loading || authLoading) {
        return (
            <div className="flex h-screen items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
        );
    }
    
    if (pageData?.error) {
        return (
            <div className="flex flex-col h-[calc(100vh-10rem)] items-center justify-center text-center">
                <p className="text-destructive mb-4">{pageData.error}</p>
                <Button onClick={fetchData}>Reintentar</Button>
            </div>
        );
    }
    
    if (!pageData || !pageData.isAuthorized) {
        return (
            <div className="flex h-screen items-center justify-center">
                <p>No tienes permisos para ver esta página o los datos no se pudieron cargar.</p>
            </div>
        );
    }

    return <ArzobispadoClientPage {...pageData} />;
}
